import React, { createContext, useContext, ReactNode } from 'react';
import {
  Cats,
  SubCats,
  NON_SHIPABLE_CATS,
  NON_SHIPABLE_SUBCATS,
} from '../generated/static-categories';
import { CategoryTagsSuccessResponseSchema, type TagPill } from '../schemas/api/search/category-tags';

interface Category {
  id: number;
  label: string;
  slug: string;
  heading?: string[];
  subheading?: string[];
  sortOrder?: number;
  parentId?: number;
}

interface CategoryGroup {
  id: number;
  label: string;
  slug: string;
  sortOrder?: number;
}

interface CategoryContextType {
  categories: Category[];
  categoryGroups: CategoryGroup[];
  loading: boolean;
  error: string | null;
  visibleCategories: number[];
  getCategoryById: (id: number) => Category | undefined;
  getCategoryBySlug: (slug: string) => Category | undefined;
  getSubcategoriesByParentId: (parentId: number) => Category[];
  isNonShipable: (categoryId: number) => boolean;
  nonShipableCategories: number[];
  nonShipableSubcategories: number[];
  /** Tag pills for a category group (tags with 20+ browsable listings). [] on any failure. */
  getTagPills: (groupId: number) => Promise<TagPill[]>;
}

// Pre-compute all data from static imports (runs once at module load)
const staticSubcategories: Category[] = Object.entries(SubCats).map(([id, cat]) => ({
  id: parseInt(id),
  label: cat.label,
  slug: cat.slug,
  heading: Array.isArray(cat.heading) ? cat.heading : [cat.label],
  subheading: Array.isArray(cat.subheading) ? cat.subheading : [],
  sortOrder: cat.sortOrder,
  parentId: cat.group,
}));

const staticCategoryGroups: CategoryGroup[] = Object.entries(Cats)
  .map(([id, cat]) => ({
    id: parseInt(id),
    label: cat.label,
    slug: cat.slug,
    sortOrder: cat.sortOrder,
  }))
  .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));

// Combine groups (as categories without parentId) and subcategories
const staticCategories: Category[] = [
  ...staticCategoryGroups.map(g => ({ ...g, parentId: undefined })),
  ...staticSubcategories,
];

// Visible categories for nav (first 10 category groups + "All")
const staticVisibleCategories = [0, ...staticCategoryGroups.slice(0, 10).map(g => g.id)];

/**
 * Pills per group for this page load. The edge caches the response for an
 * hour; this only spares a refetch when the shopper moves between a group and
 * its subcategories, which share the group's pills.
 */
const tagPillCache = new Map<number, Promise<TagPill[]>>();

async function loadTagPills(groupId: number): Promise<TagPill[]> {
  const response = await fetch(`/api/search/category-tags?groupId=${groupId}`);
  if (!response.ok) throw new Error(`category-tags ${response.status}`);
  // safeParse, not parse: a response shape from a newer or older deploy must
  // cost the pills, not the page.
  const parsed = CategoryTagsSuccessResponseSchema.safeParse(await response.json());
  return parsed.success ? parsed.data.pills : [];
}

const getTagPills = (groupId: number): Promise<TagPill[]> => {
  if (!groupId) return Promise.resolve([]);
  let pending = tagPillCache.get(groupId);
  if (!pending) {
    pending = loadTagPills(groupId).catch(() => {
      // A failed load is not remembered, so the next visit tries again.
      tagPillCache.delete(groupId);
      return [];
    });
    tagPillCache.set(groupId, pending);
  }
  return pending;
};

/** Test seam: forget cached pills. */
export const resetTagPillCache = (): void => tagPillCache.clear();

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

interface CategoryProviderProps {
  children: ReactNode;
  // Legacy props - ignored, kept for backwards compatibility
  initialCategories?: Category[];
  initialCategoryGroups?: CategoryGroup[];
  initialVisibleCategories?: number[];
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  // All data comes from static imports - no state needed
  const getCategoryById = (id: number): Category | undefined => {
    if (id === 0) return { id: 0, label: 'All', slug: '' };
    return staticCategories.find(cat => cat.id === id);
  };

  const getCategoryBySlug = (slug: string): Category | undefined => {
    if (!slug || slug === '') return { id: 0, label: 'All', slug: '' };
    return staticCategories.find(cat => cat.slug === slug);
  };

  const getSubcategoriesByParentId = (parentId: number): Category[] => {
    return staticSubcategories
      .filter(cat => cat.parentId === parentId)
      .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));
  };

  const isNonShipable = (categoryId: number): boolean => {
    return NON_SHIPABLE_CATS.includes(categoryId) || NON_SHIPABLE_SUBCATS.includes(categoryId);
  };

  const value: CategoryContextType = {
    categories: staticCategories,
    categoryGroups: staticCategoryGroups,
    loading: false, // Never loading - data is static
    error: null, // Never errors - data is static
    visibleCategories: staticVisibleCategories,
    getCategoryById,
    getCategoryBySlug,
    getSubcategoriesByParentId,
    isNonShipable,
    nonShipableCategories: NON_SHIPABLE_CATS,
    nonShipableSubcategories: NON_SHIPABLE_SUBCATS,
    getTagPills,
  };

  return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
};

export const useCategory = (): CategoryContextType => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};

export default CategoryProvider;
